import { type ReactElement, type ReactNode } from "react";
import type { SipocParams } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { SipocWidget, type SipocLinkState } from "./Component.js";

export interface SipocExpandedEditorProps extends WidgetBodyProps<SipocParams> {
  readonly linkStateByRowId?: Readonly<Record<string, SipocLinkState>>;
  /** Host-owned band placed above the board. */
  readonly offerSlot?: ReactNode;
}

/**
 * SIPOC's expanded editor. Registered through the `ExpandedComponent` seam on
 * `sipocDef` rather than as another case in `expanded-editors.tsx` — registries, not
 * switches (packages/ui CLAUDE.md invariant 3).
 *
 * The board itself is `SipocWidget` at the panel (`md`) scale. Framed the way the
 * design frames it: the modal chrome owns the ONE header row — icon chip, title,
 * the column line, the step/entry pills, comments and close — so this body owns
 * nothing above the board, which runs edge to edge. The guide line and the
 * per-column hints are the host's `SipocGuideButton`, an (i) beside the title; a
 * host that wants the pills lifts them into its own header (the VSM artifact host
 * does).
 */
export function SipocExpandedEditor({
  data,
  onChange,
  readOnly,
  linkStateByRowId,
  offerSlot,
}: SipocExpandedEditorProps): ReactElement {
  const disabled = readOnly === true;

  return (
    <div className="@container/sipoc-editor flex h-full min-h-0 flex-1 flex-col text-pb-text">
      {offerSlot ? (
        <div
          data-testid="sipoc-offer-slot"
          className="shrink-0 border-b border-pb-border px-5 py-3"
        >
          {offerSlot}
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col">
        <SipocWidget
          data={data}
          onChange={onChange}
          readOnly={disabled}
          expandedLayout
          {...(linkStateByRowId ? { linkStateByRowId } : {})}
        />
      </div>
    </div>
  );
}
